import { toast } from 'react-toastify'
import { useAgendaInteligente } from '../../../store/agendaInteligente/useAgendaInteligente'
import { useMerchant } from '../../../store/global/useMerchant'
import {
  fetchAgenda,
  fetchHistoricoCompras,
  fetchHistoricoServicos,
  updateAppointmentsContacted,
  updateAppointmentsContactStatus,
} from '../../models/agendaInteligente'
import { getAgendaContactStatus } from '../../formatResponses/agendaInteligente'
import { loadAgendaTemplate, saveAgendaTemplate } from '../../templates/agendaInteligente'

function getCustomerKey(appointment) {
  return appointment.cliente.customerId ||
    appointment.cliente.telefone ||
    appointment.cliente.cpf ||
    `${appointment.cliente.nome}-${appointment.appointmentId}`
}

function getPetKey(appointment) {
  return appointment.pet.petId ||
    `${appointment.pet.nome}-${appointment.appointmentId}`
}

class AgendaInteligenteClass {
  get actions() {
    return useAgendaInteligente.getState().actions
  }

  get state() {
    return useAgendaInteligente.getState().data
  }

  get storeId() {
    return useMerchant.getState().data.merchant?.id || null
  }

  isTemplateReady = (storeId = this.storeId) => {
    return Boolean(storeId) &&
      storeId === this.storeId &&
      storeId === this.state.templateStoreId
  }

  loadTemplate = () => {
    const storeId = this.storeId
    this.actions.setTemplateContext(storeId, loadAgendaTemplate(storeId))
  }

  fetchData = async () => {
    this.actions.setLoading(true)
    this.actions.setError(null)
    try {
      const result = await fetchAgenda(this.state.filter)
      this.actions.setAgenda(result)
    } catch (error) {
      const message =
        error?.response?.data?.friendly_message ||
        'Não foi possível carregar a agenda. Tente novamente.'
      this.actions.setError(message)
    } finally {
      this.actions.setLoading(false)
    }
  }

  setActiveTab = (tab) => {
    this.actions.setActiveTab(tab)
  }

  setFilter = (filter) => {
    this.actions.setFilter(filter)
  }

  clearFilter = () => {
    this.actions.setFilter({ status: 'todos', type: 'todos', query: '' })
  }

  toggleExpanded = (appointmentId) => {
    const current = this.state.expandedId
    this.actions.setExpandedId(current === appointmentId ? null : appointmentId)
  }

  setContactStatus = async (appointmentId, status) => {
    const previousValue =
      this.state.contactStatus[appointmentId] ||
      Object.values(this.state.agendamentos)
        .flat()
        .find(appointment => appointment.appointmentId === appointmentId)
        ?.status ||
      'pendente'

    this.actions.setContactStatus(appointmentId, status)

    const updated = await updateAppointmentsContactStatus([appointmentId], status)
    if (!updated) {
      this.actions.setContactStatus(appointmentId, previousValue)
      return false
    }

    return true
  }

  setContactDone = async (appointmentId, done) => {
    await this.markAppointmentsContacted([appointmentId], done)
  }

  markAppointmentsContacted = async (appointmentIds, contacted = true) => {
    const ids = [...new Set((appointmentIds || []).filter(Boolean))]
    if (ids.length === 0) return false

    const previousValues = ids.reduce((values, appointmentId) => {
      values[appointmentId] = Boolean(this.state.contactDone[appointmentId])
      return values
    }, {})

    this.actions.setContactDoneMany(ids, contacted)

    const updated = await updateAppointmentsContacted(ids, contacted)
    if (!updated) {
      ids.forEach(appointmentId => {
        this.actions.setContactDone(appointmentId, previousValues[appointmentId])
      })
      return false
    }

    return true
  }

  loadHistory = async (appointment) => {
    const id = appointment.appointmentId
    const cache = this.state.historyCache
    if (cache[id] !== undefined) return
    this.actions.setHistoryLoading(id)
    const [services, purchases] = await Promise.allSettled([
      appointment.pet.petId
        ? fetchHistoricoServicos(appointment.pet.petId)
        : Promise.resolve([]),
      appointment.cliente.cpf
        ? fetchHistoricoCompras(appointment.cliente.cpf)
        : Promise.resolve([]),
    ])

    this.actions.setHistory(id, {
      servicos: services.status === 'fulfilled' ? services.value : [],
      compras: purchases.status === 'fulfilled' ? purchases.value : [],
    })
  }

  openWhatsApp = (appointmentId) => {
    if (!this.isTemplateReady()) return
    this.actions.setWaApptId(appointmentId)
  }

  closeWhatsApp = () => {
    this.actions.setWaApptId(null)
  }

  openSettings = () => {
    if (!this.isTemplateReady()) return
    this.actions.setSettingsOpen(true)
  }

  closeSettings = () => {
    this.actions.setSettingsOpen(false)
  }

  saveTemplate = (template, storeId = this.state.templateStoreId) => {
    if (!this.isTemplateReady(storeId)) {
      toast.error('A loja mudou ou ainda não foi carregada. Reabra as configurações e tente novamente.', { theme: 'colored' })
      return false
    }

    if (!saveAgendaTemplate(template, storeId)) {
      toast.error('Não foi possível salvar a mensagem neste navegador. Tente novamente.', { theme: 'colored' })
      return false
    }

    this.actions.setTemplate(template)
    this.closeSettings()
    return true
  }

  getContactStatus = (apt) => {
    return getAgendaContactStatus(this.state.contactStatus, apt)
  }

  getRows = () => {
    const { agendamentos, activeTab, filter } = this.state
    const appointments = agendamentos[activeTab] || []
    const petKeysByCustomer = appointments.reduce((customers, appointment) => {
      const customerKey = getCustomerKey(appointment)
      const petKey = getPetKey(appointment)

      if (!customers.has(customerKey)) customers.set(customerKey, new Set())
      customers.get(customerKey).add(petKey)
      return customers
    }, new Map())

    return appointments.map(appointment => ({
      ...appointment,
      scheduledPetsCount:
        petKeysByCustomer.get(getCustomerKey(appointment))?.size || 1,
    }))
  }

  getStats = () => {
    const { agendamentos, activeTab, contactStatus } = this.state
    const apts = agendamentos[activeTab] || []
    return {
      total: apts.length,
      confirmados: apts.filter(a => a.status === 'confirmado' || getAgendaContactStatus(contactStatus, a) === 'confirmado').length,
      cancelados: apts.filter(a => a.status === 'cancelado' || getAgendaContactStatus(contactStatus, a) === 'cancelado').length,
      assinantes: apts.filter(a => a.assinante).length,
      pacotes: apts.filter(a => a.package || a.isPackage).length,
      avulsos: apts.filter(a => !a.assinante && !a.package).length,
      novos: apts.filter(a => a.cliente.primeiroAtendimento).length,
    }
  }
}

let instance = null

export function AgendaInteligenteController() {
  if (!instance) instance = new AgendaInteligenteClass()
  return instance
}
